import { msg } from "@lingui/core/macro";
import { type Component, Show } from "solid-js";
import { AlertCircle, RefreshCw } from "@fleet/icons";
import { cn } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "./button";

interface QueryErrorStateProps {
  /**
   * `data-testid` of the error box. Convention: `<screen>-error`.
   * It is the anchor for the I3 spec: the probe requires THIS testid to be visible and
   * the EMPTY-state testid to have `toHaveCount(0)`.
   */
  testId: string;
  /** Custom title. Default: "Couldn't load". */
  title?: string;
  /** Custom description. Default: explains that it is a connection failure. */
  description?: string;
  /**
   * Retry. It must ACTUALLY retry (`query.refetch()`), not reload the
   * page or clear the state: a button that does not request again is as misleading
   * as having no error state.
   */
  onRetry?: () => void;
  /** Compact variant for sections inside a `<Card>`. */
  compact?: boolean;
  class?: string;
}

/**
 * ERROR state of a query — the third state that is almost never written.
 *
 * P23 / CC-60 · invariant I3: "no response" ≠ "no data". A screen
 * with only loading + empty paints a network failure as "you have no items", and
 * lies to the user about the state of their own product. In the profile this
 * used to say "No active sessions found" when the API had not responded:
 * a lie with security consequences (the user believes nobody else
 * has an open session).
 *
 * Deliberately does NOT reuse `EmptyState`: it should look as LITTLE like the
 * empty state as possible. Destructive-tone alert icon, its own border, and an
 * explicit retry.
 */
export const QueryErrorState: Component<QueryErrorStateProps> = (props) => {
  const { t } = useI18n();

  return (
    <div
      data-testid={props.testId}
      role="alert"
      class={cn(
        "flex flex-col items-center gap-3 rounded-xl border border-destructive/25 bg-destructive/5 text-center",
        props.compact ? "p-4" : "p-8",
        props.class,
      )}
    >
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertCircle class="h-5 w-5" />
      </div>
      <div class="space-y-1">
        <p class="text-sm font-semibold text-foreground">
          {props.title ?? t(msg`Couldn't load this`)}
        </p>
        <p class="max-w-xs text-xs text-muted-foreground text-balance">
          {props.description ??
            t(msg`We couldn't reach the server. This is a connection problem, not empty data.`)}
        </p>
      </div>
      <Show when={props.onRetry}>
        {(retry) => (
          <Button
            variant="outline"
            size="sm"
            class="gap-2"
            onClick={() => {
              retry()();
            }}
          >
            <RefreshCw class="h-3.5 w-3.5" />
            {t(msg`Retry`)}
          </Button>
        )}
      </Show>
    </div>
  );
};
